import type { AuctisyncSupabase } from './client.js';

/**
 * Registering to bid with a house.
 *
 * AN ACCOUNT IS NOT PERMISSION. Having signed in to AuctiSync does not entitle
 * anyone to bid in a particular sale — the house decides who bids in its own
 * auction, and on an auction with `requires_bidder_approval` that decision is a
 * person's, not a rule's.
 *
 * WHAT IT IS DECIDING ON is a name, a reachable phone, a billing address and a
 * card. Collected once and kept for every later sale, which is why this is
 * worth a proper flow rather than a note field: an auctioneer approving a
 * stranger wants to see who they are, and a bidder should not be asked twice.
 *
 * ⚠️ NOT EVERY AUCTION NEEDS THIS. `requires_bidder_approval` is per-auction and
 * defaults to false; `place_bid` gates on it before anything else, and for a
 * house that does not require approval it simply records the buyer on first
 * bid. Showing a registration flow where none is required would invent
 * friction the house deliberately did not ask for.
 */

export interface RegistrationDetails {
  displayName: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
}

export type DetailsField = keyof RegistrationDetails;

/**
 * What is missing, field by field.
 *
 * Returns the FIELDS rather than a single message so a form can mark the ones
 * at fault. A flow that says only "please check your details" makes someone
 * hunt through four inputs for the one they missed.
 */
export function validateRegistrationDetails(details: RegistrationDetails): DetailsField[] {
  const missing: DetailsField[] = [];
  if (details.displayName.trim().length < 2) missing.push('displayName');
  // Digits, not characters: people type "(417) 555-0134" and a length check on
  // the raw string passes things that are not phone numbers while failing
  // things that are.
  if (digits(details.phone).length < 10) missing.push('phone');
  if (details.addressLine1.trim().length < 3) missing.push('addressLine1');
  if (details.city.trim().length < 2) missing.push('city');
  if (details.region.trim().length < 2) missing.push('region');
  if (digits(details.postalCode).length < 5) missing.push('postalCode');
  return missing;
}

function digits(value: string): string {
  return value.replace(/\D/g, '');
}

/** Human wording for a missing field, shared so both surfaces say the same thing. */
export function describeMissingField(field: DetailsField): string {
  switch (field) {
    case 'displayName':
      return 'Enter the name the house should know you by.';
    case 'phone':
      return 'Enter a phone number the house can reach you on.';
    case 'addressLine1':
      return 'Enter your billing street address.';
    case 'city':
      return 'Enter your city.';
    case 'region':
      return 'Enter your state or region.';
    case 'postalCode':
      return 'Enter your postal code.';
    default:
      return 'This is required.';
  }
}

export type ApprovalStatus = 'none' | 'pending' | 'approved' | 'rejected' | 'revoked';

export interface RegistrationState {
  approvalStatus: ApprovalStatus;
  /** Whether the house requires approval for THIS auction at all. */
  required: boolean;
}

export type RegistrationStateResult =
  | { ok: true; state: RegistrationState }
  | { ok: false; message: string };

/**
 * Where this bidder stands with this auction.
 *
 * Reads the auction's requirement as well as the request, because "no approval
 * row" means two completely different things depending on it: nothing to do, or
 * nothing done yet.
 */
export async function fetchRegistrationState(
  supabase: AuctisyncSupabase,
  params: { auctionId: string; userId: string },
): Promise<RegistrationStateResult> {
  const [auction, approval] = await Promise.all([
    supabase
      .from('auctions')
      .select('requires_bidder_approval')
      .eq('id', params.auctionId)
      .maybeSingle(),
    supabase
      .from('bidder_approvals')
      .select('status')
      .eq('auction_id', params.auctionId)
      .eq('user_id', params.userId)
      .maybeSingle(),
  ]);

  if (auction.error) return { ok: false, message: auction.error.message };

  const required =
    (auction.data as { requires_bidder_approval?: unknown } | null)?.requires_bidder_approval ===
    true;
  const raw = (approval.data as { status?: unknown } | null)?.status;
  const status: ApprovalStatus =
    raw === 'pending' || raw === 'approved' || raw === 'rejected' || raw === 'revoked'
      ? raw
      : 'none';

  return { ok: true, state: { approvalStatus: status, required } };
}

export type SubmitResult = { ok: true } | { ok: false; message: string };

/**
 * Save the details the house will judge, then ask.
 *
 * ORDER MATTERS. The profile is written FIRST: a pending request pointing at an
 * empty profile gives the auctioneer a name-less row to approve, which is the
 * one thing this flow exists to avoid. If the profile write fails, no request is
 * made at all.
 */
export async function submitRegistration(
  supabase: AuctisyncSupabase,
  params: {
    tenantId: string;
    auctionId: string;
    userId: string;
    details: RegistrationDetails;
    note?: string;
  },
): Promise<SubmitResult> {
  const missing = validateRegistrationDetails(params.details);
  if (missing.length > 0) {
    return { ok: false, message: describeMissingField(missing[0] as DetailsField) };
  }

  const { error: profileError } = await supabase
    .from('profiles')
    .update({
      display_name: params.details.displayName.trim(),
      phone: params.details.phone.trim(),
      address_line1: params.details.addressLine1.trim(),
      address_line2: params.details.addressLine2.trim(),
      city: params.details.city.trim(),
      region: params.details.region.trim(),
      postal_code: params.details.postalCode.trim(),
    })
    .eq('id', params.userId);

  if (profileError) return { ok: false, message: profileError.message };

  const { error } = await supabase.from('bidder_approvals').insert({
    tenant_id: params.tenantId,
    auction_id: params.auctionId,
    user_id: params.userId,
    status: 'pending',
    request_note: params.note?.trim() || null,
  });

  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/**
 * What to tell the bidder about where they stand.
 *
 * Shared, because these sentences decide whether someone waits patiently or
 * assumes the app is broken — and a bidder reading one story on their phone and
 * another on the website has no way to know which is true.
 */
export function describeApprovalStatus(
  status: ApprovalStatus,
  houseName: string | null,
): { tone: 'success' | 'notice' | 'error'; title: string; body: string } {
  const house = houseName ?? 'The auction house';
  switch (status) {
    case 'approved':
      return {
        tone: 'success',
        title: "You're approved to bid",
        body: `${house} has approved you for this sale.`,
      };
    case 'pending':
      return {
        tone: 'notice',
        title: 'Registration sent',
        body:
          `${house} reviews each bidder itself, so this is not instant. You'll be notified as ` +
          `soon as it's decided — you don't need to keep this screen open.`,
      };
    case 'rejected':
      return {
        tone: 'error',
        title: 'This house has declined your registration',
        body: `${house} did not approve you for this sale. Contact them directly if that seems wrong.`,
      };
    case 'revoked':
      return {
        tone: 'error',
        title: 'Your approval was withdrawn',
        body: `${house} has withdrawn your approval for this sale.`,
      };
    case 'none':
      return {
        tone: 'notice',
        title: 'Register to bid',
        body: `${house} approves its own bidders. Register once and it carries to their later sales.`,
      };
  }
}
